import React, { useEffect, useState } from 'react';
import ShareButton from './ShareButton.js';
import { API_BASE_URL } from '../scripts/control';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import OptimizedImage from '../../../components/OptimizedImage';
import RichTextInput from './RichTextInput';
import { linkifyContent } from '../../../utils/linkifyHtml';
import '../../css/MemberCard.css';

function prepareBioHtml(bio) {
    if (!bio || bio === 'undefined') return '';
    if (/<\/?(?:p|br|div|ul|ol|li|h[1-6])\b/i.test(bio)) {
        return linkifyContent(bio);
    }
    const withBreaks = bio.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>');
    return linkifyContent(withBreaks);
}

const MemberCard = () => {
    const [members, setMembers] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [deletingMember, setDeletingMember] = useState(null);
    const [bioContent, setBioContent] = useState('');
    const { isAdmin } = useAdminAuth();

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/member_func.php`)
        .then(async response => {
          const text = await response.text();
          try {
            return JSON.parse(text);
          } catch(err) {
            throw err;
          }
        })
        .then(data => {
                if (data.error) {
                } else {
                    setMembers(data);
                    setExpanded(data.reduce((acc, member) => ({ ...acc, [member.idmember]: false }), {}));
                }
            })
            .catch(() => {});
    }, []);

    const handleEdit = (member) => {
        setEditingMember(member);
        const bio = member.bio && member.bio !== 'undefined' ? member.bio : '';
        setBioContent(bio);
    };

    const handleDelete = (member) => {
        setDeletingMember(member);
        setShowConfirmDeleteModal(true); // Abrir modal de confirmación de borrado
    };

    const confirmDelete = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/delete_member.php`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: deletingMember.idmember }),
            });

            if (response.ok) {
                setMembers(members.filter(member => member.idmember !== deletingMember.idmember));
                setDeletingMember(null);
                setShowConfirmDeleteModal(false); // Cerrar modal
            } else {
            }
        } catch (error) {
        }
    };

    const cancelDelete = () => {
        setDeletingMember(null);
        setShowConfirmDeleteModal(false); // Cerrar modal de confirmación de borrado
    };

    const handleCloseModal = () => {
        setShowConfirmCloseModal(true);
    };

    const handleConfirmClose = () => {
        setEditingMember(null);
        setShowConfirmCloseModal(false);
    };

    const handleCancelClose = () => {
        setShowConfirmCloseModal(false);
    };

    const handleSave = async () => {
        const formData = new FormData();
        const name = document.getElementById('name').value;
        const surname = document.getElementById('surname').value;
        const role = document.getElementById('role').value;
        const bio = bioContent ?? '';
        const scholar = document.getElementById('scholar').value;
        const orcid = document.getElementById('orcid').value;
        const prisma = document.getElementById('prisma').value;
        const imgFile = document.getElementById('img_url').files[0];
        const imgAlt = document.getElementById('img_alt').value;

        formData.append('id', editingMember.idmember);
        formData.append('name', name);
        formData.append('surname', surname);
        formData.append('role', role);
        formData.append('bio', bio);
        formData.append('scholar', scholar);
        formData.append('orcid', orcid);
        formData.append('prisma', prisma);
        formData.append('img_alt', imgAlt);
        if (imgFile) {
            formData.append('img_url', imgFile);
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/update_member.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const responseText = await response.text();
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (e) {
                alert('Error del servidor (respuesta no válida): ' + responseText.substring(0, 300));
                return;
            }

            if (!response.ok) {
                alert('Error HTTP ' + response.status + ': ' + (data.message || 'Error desconocido'));
                return;
            }

            if (!data.success) {
                alert('Error al guardar: ' + (data.message || 'El servidor no confirmó el guardado'));
                return;
            }

            const finalImgUrl = data.img_url != null ? data.img_url : (imgFile ? imgFile.name : editingMember.img_url);
            const updatedMembers = members.map(member =>
                member.idmember === editingMember.idmember
                    ? {
                          ...member,
                          name,
                          surname,
                          role,
                          bio,
                          scholar,
                          orcid,
                          prisma,
                          img_url: finalImgUrl,
                          img_alt: imgAlt,
                      }
                    : member
            );
            setMembers(updatedMembers);
            setEditingMember(null);
            window.location.reload();
        } catch (error) {
            alert('Error de red al guardar: ' + error.message);
        }
    };

    const formatDate = (dateString) => {
        const options = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };
        return new Date(dateString).toLocaleString('es-ES', options);
    };

    return (
        <div className="members-cards-container">
            {members.map((member) => {
                const imgSrc = member.img_url
                    ? `${API_BASE_URL}/public/uploads/images/${member.img_url}`
                    : `${API_BASE_URL}/public/uploads/images/comic.jpg`;

                return (
                    <div
                        className={`members-card ${expanded[member.idmember] ? 'expanded' : ''}`}
                        key={member.idmember}
                        id={`card-${member.idmember}`}
                    >
                        <h5 className="members-card-title">{member.name} {member.surname}</h5>
                        <OptimizedImage className="member-img" src={imgSrc} alt={member.img_alt || 'Imagen por defecto'} loading="lazy" aspectRatio={1} wrapperClassName="optimized-image-round" />
                        <p className="members-card-text">
                                <small className="members-text-muted">{member.role}</small>
                            </p>
                        <div className="members-card-body bio">
                            <div dangerouslySetInnerHTML={{ __html: prepareBioHtml(member.bio) }} />
                            {/* Redes sociales */}
                            <div className="members-social-icons">
                                {member.scholar && (
                                    <a href={member.scholar} target="_blank" rel="noopener noreferrer">
                                        <img src="https://grupo.us.es/ligaincom/public/uploads/images/scholar.png" 
                                        alt="Google Scholar" style={{ width: '55px', height: '50px' }} />
                                    </a>
                                )}
                                {member.orcid && (
                                    <a href={member.orcid} target="_blank" rel="noopener noreferrer">
                                        <img src="https://grupo.us.es/ligaincom/public/uploads/images/orcid.png" 
                                        alt="ORCID" style={{ width: '60px', height: '40px' }} />
                                    </a>
                                )}
                                {member.prisma && (
                                    <a href={member.prisma} target="_blank" rel="noopener noreferrer">
                                        <img src="https://grupo.us.es/ligaincom/public/uploads/images/prisma.png" 
                                        alt="Prisma" style={{ width: '60px', height: '40px' }} />
                                    </a>
                                )}
                            </div>

                            {isAdmin && (
                                <div style={{ marginTop: '10px' }}>
                                    <button
                                        onClick={() => handleEdit(member)}
                                        style={{
                                            marginRight: '10px',
                                            padding: '5px 10px',
                                            backgroundColor: '#ffc107',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => handleDelete(member)}
                                        style={{
                                            padding: '5px 10px',
                                            backgroundColor: '#dc3545',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            color: 'white',
                                        }}
                                    >
                                        Borrar
                                    </button>
                                </div>
                            )}
                        </div >
                        <hr />
                    </div>
                );
            })}

            {/* Modal para editar el integrante */}
            {editingMember && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Editar integrante</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCloseModal}></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <label htmlFor="name">Nombre:</label><br />
                                    <input type="text" id="name" name="name" defaultValue={editingMember.name} /><br />
                                    <label htmlFor="name">Apellidos:</label><br />
                                    <input type="text" id="surname" name="surname" defaultValue={editingMember.surname} /><br />
                                    <label htmlFor="role">Rol:</label><br />
                                    <input type="text" id="role" name="role" defaultValue={editingMember.role} /><br />
                                    <label htmlFor="bio">Bio:</label><br />
                                    <RichTextInput id="bio" initialContent={bioContent} onChange={setBioContent} placeholder="Biografía..." editorKey={`member-edit-${editingMember?.idmember}-bio`} style={{ minHeight: '120px' }} /><br />
                                    <label htmlFor="scholar">Google Scholar:</label><br />
                                    <input type="text" id="scholar" name="scholar" defaultValue={editingMember.scholar} /><br />
                                    <label htmlFor="orcid">Orcid:</label><br />
                                    <input type="text" id="orcid" name="orcid" defaultValue={editingMember.orcid} /><br />
                                    <label htmlFor="prisma">Prisma:</label><br />
                                    <input type="text" id="prisma" name="prisma" defaultValue={editingMember.prisma} /><br />
                                    <label htmlFor="img_url">Imagen:</label><br />
                                    <input type="file" id="img_url" name="img_url" accept="image/*" /><br />
                                    <label htmlFor="img_alt">Texto alternativo de la imagen:</label><br />
                                    <input type="text" id="img_alt" name="img_alt" defaultValue={editingMember.img_alt}/><br />
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancelar</button>
                                <button type="button" className="btn btn-primary" onClick={handleSave}>Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de cierre */}
            {showConfirmCloseModal && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar cierre</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCancelClose}></button>
                            </div>
                            <div className="modal-body">
                                <p>¿Quieres abandonar la edición? Se perderán los cambios no guardados.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCancelClose}>Volver</button>
                                <button type="button" className="btn btn-primary" onClick={handleConfirmClose}>Salir</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de borrado */}
            {showConfirmDeleteModal && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar borrado</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                <p>¿Estás seguro de que deseas borrar el integrante {deletingMember?.name} {deletingMember?.surname}?</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>Cancelar</button>
                                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Borrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MemberCard;
